import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

// Retry once with 1.5s delay on 502/503/504 (handles transient backend restarts)
client.interceptors.response.use(undefined, async (err) => {
    const cfg = err.config;
    if (cfg && !cfg.__retried && [502, 503, 504].includes(err.response?.status)) {
        cfg.__retried = true;
        await new Promise((r) => setTimeout(r, 1500));
        return client(cfg);
    }
    return Promise.reject(err);
});

client.interceptors.request.use((config) => {
    const token = localStorage.getItem("fp_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

client.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err.response?.status === 401 && !window.location.pathname.includes("/login")) {
            localStorage.removeItem("fp_token");
            localStorage.removeItem("fp_user");
            window.location.href = "/login";
        }
        // Silence AI insights 5xx AND network-level failures (502/503/504) — panels handle gracefully
        if (err.config?.url?.includes("/ai/insights") || [502, 503, 504].includes(err.response?.status) || err.code === "ERR_NETWORK") {
            console.warn("API transient error:", err.message);
            return Promise.reject({ ...err, __silent: true });
        }
        return Promise.reject(err);
    }
);

export default client;

export const streamAIInsights = async (context, question, onDelta, onDone, onError, signal) => {
    const token = localStorage.getItem("fp_token");
    try {
        const res = await fetch(`${API}/ai/insights`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ context, question }),
            signal,
        });
        if (!res.ok) { onError?.("AI briefing unavailable (LLM budget exhausted — top up Universal Key)"); return; }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";
            for (const line of lines) {
                if (line.startsWith("data: ")) {
                    const raw = line.slice(6);
                    if (raw === "[DONE]") { onDone?.(); return; }
                    if (raw.startsWith("[ERROR]")) { onError?.(raw); return; }
                    // Un-escape newlines that the backend serialised for SSE compatibility
                    const payload = raw.replace(/\\n/g, "\n").replace(/\\\\/g, "\\");
                    onDelta?.(payload);
                }
            }
        }
        onDone?.();
    } catch (e) {
        if (e.name === "AbortError") return;
        onError?.(e.message);
    }
};
